import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { MilestoneSummary, TicketDetail } from "@kundeninfo/api-client";
import { Chip, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { ObjektVerweis } from "../../components/objektPanel/ObjektVerweis.js";
import { heuteBeschriftung } from "../tickets/ticketFormat.js";
import { LinkSearch } from "./LinkSearch.js";
import type { MilestonesClient } from "./milestonesClient.js";
import { MILESTONE_TEXT } from "./milestoneTexts.js";

/**
 * ABSCHNITT „MEILENSTEINE“ IM REITER „VERKNÜPFUNGEN“ (Spec D21, Aufgabe 8; aus `TicketVerknuepfungen.tsx` hierher gezogen,
 * Review G4): die verknüpften Meilensteine des Tickets (`listTicketMilestones`) mit Stichtag, Zustand als Kennzeichen und
 * Fortschritt; der Meilenstein ist ein fremdes Objekt (K9): Titel als Text, der Verweis dahinter öffnet das Panel über den
 * Weg des Aufrufers (`onOpen`; Bereich bzw. Gastgeber legen es seitlich über den Dialog). „Mit Meilenstein verknüpfen …“
 * sucht (`LinkSearch`) die OFFENEN Meilensteine des Ticket-Unternehmens (`states: ["OPEN"]`, sonst verdrängten alte
 * erreichte die kommenden). Nach dem Verknüpfen holt der Dialog das Ticket nach (Verlauf `MILESTONE_ADDED`) — das neue `d`
 * lädt die Liste neu; nur ohne `nachladen` bzw. wenn es scheitert, lädt der Abschnitt selbst (Review G1: ein Abruf).
 * Nur die jüngste Antwort zeichnet.
 */

const T = MILESTONE_TEXT;
const STATE_ART: Readonly<Record<MilestoneSummary["state"], "laeuft" | "ok" | "aus">> =
  { OPEN: "laeuft", REACHED: "ok", CANCELLED: "aus" };

export function TicketMilestones(p: { d: TicketDetail; client: MilestonesClient; onOpen: (m: { id: string; title: string }) => void;
                                      onNeu: (d: TicketDetail) => void; nachladen?: () => Promise<TicketDetail> }): ReactElement {
  const [items, setItems] = useState<MilestoneSummary[] | "error" | null>(null);
  const [searching, setSearching] = useState(false);
  const { client, d } = p;
  const run = useRef(0);
  const load = useCallback(() => {
    const n = ++run.current;
    client.listTicketMilestones(d.id)
      .then((r) => { if (run.current === n) setItems(r.items); })
      .catch(() => { if (run.current === n) setItems("error"); });
  }, [client, d]);
  useEffect(() => { load(); }, [load]);
  const companyId = d.companyId;
  const search = useCallback(async (q: string) => (await client.searchMilestones({ company: companyId,
    ...(q === "" ? {} : { q }), states: ["OPEN"] })).items, [client, companyId]);
  const list = Array.isArray(items) ? items : [];

  async function link(milestoneId: string): Promise<string | null> {
    try {
      await client.linkMilestoneTicket(milestoneId, d.id);
    } catch (e) {
      return e instanceof ApiError && e.message !== "" ? e.message : T.linkError;
    }
    if (p.nachladen === undefined) load();
    else void p.nachladen().then(p.onNeu, load);
    return null;
  }

  return (
    <section className="td__abschnitt td__milestones" aria-label={T.section}>
      <div className="td__abschnittkopf">
        <h4 className="td__ueberschrift">{T.section}</h4>
        <KiKnopf kompakt deaktiviert={items === null} onClick={() => setSearching(true)}>{T.linkMilestone}</KiKnopf>
      </div>
      {items === "error" && <Hinweis art="warnung">{T.sectionLoadError}</Hinweis>}
      {Array.isArray(items) && items.length === 0 && <p className="td__leer">{T.sectionEmpty}</p>}
      {list.length > 0 && (
        <ul className="td__liste">
          {list.map((m) => (
            <li key={m.id} className="td__milestone">
              <span>{m.title}</span>
              <ObjektVerweis bezug={{ art: "meilenstein", id: m.id, label: m.title, companyId: d.companyId }}
                eigenesPanel={() => p.onOpen({ id: m.id, title: m.title })} />
              <span className="td__neben">{heuteBeschriftung(m.dueDate)}</span>
              <Chip statusArt={STATE_ART[m.state]}>{T.states[m.state]}</Chip>
              <span className="td__neben">{T.progress(m.progress.done, m.progress.total)}</span>
            </li>
          ))}
        </ul>
      )}
      {searching && (
        <LinkSearch title={T.linkMilestoneTitle} searchName={T.milestoneSearch} placeholder={T.milestoneSearchHint}
          search={search} immediate label={(m) => T.milestoneHit(m.title, heuteBeschriftung(m.dueDate))}
          linked={new Set(list.map((m) => m.id))} emptyText={T.noOpenMilestones} onLink={link}
          onDone={() => setSearching(false)} />
      )}
    </section>
  );
}
